import React, { useRef, useState } from "react";
import { api } from "../../lib/api";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";

export default function ImageUploader({ value, onChange, multiple = false, folder = "bcpeabody/uploads", label }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const urls = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const uploadOne = async (file) => {
    const sigRes = await api.get(`/admin/cloudinary/signature?folder=${encodeURIComponent(folder)}`);
    const { signature, timestamp, cloud_name, api_key, folder: signedFolder } = sigRes.data;
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", api_key);
    form.append("timestamp", timestamp);
    form.append("signature", signature);
    form.append("folder", signedFolder);

    const res = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, {
      method: "POST",
      body: form,
    });
    if (!res.ok) throw new Error("Cloudinary upload failed");
    const data = await res.json();
    return data.secure_url;
  };

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (!list.length) return;

    // Client-side validation
    const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
    for (const f of list) {
      if (!f.type.startsWith("image/")) {
        alert(`"${f.name}" is not an image. Please upload JPG, PNG, or WebP files.`);
        return;
      }
      if (f.size > MAX_BYTES) {
        alert(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)} MB, please keep images under 10 MB.`);
        return;
      }
    }

    setUploading(true);
    setProgress(0);
    try {
      const out = [];
      for (let i = 0; i < list.length; i++) {
        const url = await uploadOne(list[i]);
        out.push(url);
        setProgress(Math.round(((i + 1) / list.length) * 100));
      }
      if (multiple) {
        onChange([...urls, ...out]);
      } else {
        onChange(out[0]);
      }
    } catch (e) {
      alert("Upload failed: " + (e.message || "please try again"));
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    if (multiple) {
      onChange(urls.filter((_, i) => i !== idx));
    } else {
      onChange("");
    }
  };

  return (
    <div data-testid={`uploader-${folder.split('/').pop()}`}>
      {label && <div className="overline mb-2">{label}</div>}

      {/* Previews */}
      {urls.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {urls.map((u, i) => (
            <div key={u + i} className="relative group aspect-square bg-cream-light border border-ink/10 overflow-hidden">
              <img src={u} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 bg-ink/80 text-cream-light p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload zone */}
      <label
        htmlFor={`file-${folder}`}
        className={`flex items-center justify-center gap-3 border-2 border-dashed border-ink/20 hover:border-copper hover:bg-cream-light cursor-pointer p-6 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
      >
        {uploading ? (
          <>
            <Loader2 className="text-copper animate-spin" size={20} />
            <span className="text-sm text-ink/70">Uploading… {progress}%</span>
          </>
        ) : (
          <>
            <Upload className="text-copper" size={20} />
            <span className="text-sm text-ink/70">
              {multiple ? "Drop or click to upload images" : urls.length ? "Replace image" : "Drop or click to upload an image"}
            </span>
          </>
        )}
      </label>
      <input
        id={`file-${folder}`}
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
